import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { resolveProfilePictureUrl } from '../utils/profilePicture';
import { getLoginUniqueId } from '../../../helpers/commandCentreRouting';
import { updateTicket, updateTicketStatus } from '../../../services/ticketService';
import Conversation from '../../Conversation/Conversation';
import '../TicketDetailsPanel/TicketDetailsModal.css';
import { Ticket, TicketStatus } from '../utils/commandCenterTypes';
import { formatTaskTypeLabel } from '../utils/taskTypes';
import { normalizeTicketStatus } from '../utils/ticketFilters';

const STATUS_OPTIONS: Array<{ value: TicketStatus; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'on-hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
];

const getSafeHttpUrl = (value?: string | null): string | null => {
  if (!value) return null;

  try {
    const parsed = new URL(value);

    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return null;
    }

    return parsed.toString();
  } catch {
    return null;
  }
};

interface TicketDetailsPanelProps {
  ticket: any;
  commandCentreAuth: any;
  appLoading: any;
  availableUsers?: any[];

  /**
   * Optional: callback when assignees change (for parent state, if needed).
   */
  onChangeAssignees?: (
    nextAssignees: any[],
    meta?: {
      assigneeIds: number[];
      newlyAddedIds: number[];
      notifyUserIds: number[];
    }
  ) => void;

  /** Participants with display_name + profile_picture, from backend */
  entityDisplayData: any[];
  onTicketActivity?: (ticketId: number) => void;
  onTicketUpdated?: (ticket: Ticket) => void;

}

const TicketDetailsPanel: React.FC<TicketDetailsPanelProps> = ({
  ticket,
  commandCentreAuth,
  appLoading,
  availableUsers,
  onChangeAssignees,
  entityDisplayData,
  onTicketActivity,
  onTicketUpdated
}) => {
  // 🔹 Local UI state
  const [showAssigneeManager, setShowAssigneeManager] = useState(false);
  const [assignees, setAssignees] = useState<any[]>([]);
  const [selectedUserIdToAdd, setSelectedUserIdToAdd] = useState<number | ''>('');
  const [originalAssigneeIds, setOriginalAssigneeIds] = useState<number[]>([]);
  const [notifyOnAdd, setNotifyOnAdd] = useState<Record<number, boolean>>({});
  const [savingAssignees, setSavingAssignees] = useState(false);
  const [assigneeSaveError, setAssigneeSaveError] = useState<string | null>(null);
  const [failedAvatarIds, setFailedAvatarIds] = useState<Record<string, boolean>>({});
  const [statusValue, setStatusValue] = useState<TicketStatus>('open');
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusSaveError, setStatusSaveError] = useState<string | null>(null);

  // 🔹 Normalize assignees from a ticket into *full* user objects
  const normalizeAssignees = (t: any, pool: any[] | undefined, entityPool: any[]) => {
    if (!t) return [];
  
    let raw: any[] = [];
    if (Array.isArray(t.assignees) && t.assignees.length > 0) {
      raw = t.assignees;
    } else if (t.assignee) {
      raw = [t.assignee];
    }
  
    if (raw.length === 0) return [];
  
    return raw.map((item) => {
      const id = typeof item === "number" ? item : item?.id;
      if (!id) return item;
  
      const type = item?.type || "user";
  
      // first try: availableUsers (for user-type)
      let found =
        pool?.find((u) => u.id === id) ||
        entityPool?.find((e) => e.id === id && e.type === type);
  
      if (found) return found;
  
      // if item already looks enriched, keep it
      if (
        item &&
        (item.display_name ||
          item.name ||
          item.full_name ||
          item.email_address ||
          item.first_name)
      ) {
        return item;
      }
  
      // fallback placeholder
        return {
          id,
          type,
          display_name:
          type === "instance"
            ? `Instance #${id}`
            : `User #${id}`,
      };
    });
  };
  

  // 🔹 When ticket changes, reset local assignees + "original" ids
  useEffect(() => {
    const normalized = normalizeAssignees(ticket, availableUsers, entityDisplayData);
    setAssignees(normalized);
  
    const origIds = normalized
      .filter((a: any) => a && a.id != null)
      .map((a: any) => Number(a.id));
  
    setOriginalAssigneeIds(origIds);
    setNotifyOnAdd({});
  }, [ticket, availableUsers, entityDisplayData]);  

  useEffect(() => {
    setStatusValue(normalizeTicketStatus(ticket?.task_status) as TicketStatus);
    setStatusSaveError(null);
  }, [ticket?.task_status]);

  // 🔹 Match backend isAdmin semantics
  const canManageAssignees =
    commandCentreAuth?.status === true &&
    commandCentreAuth?.isAdministrator === true &&
    commandCentreAuth?.scope !== 'none';
  const canUpdateStatus =
    commandCentreAuth?.status === true && commandCentreAuth?.scope !== 'none';

  const assigneeCount = assignees.length;

  const getDisplayName = (u: any) =>
    u.display_name ||
    u.name ||
    u.full_name ||
    `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
    u.email_address ||
    `User #${u.id ?? ''}`;

  const getEmail = (u: any) => u.email_address || u.email || '';

  const getInitials = (u: any) => {
    const first = (u?.first_name || '').trim();
    const last = (u?.last_name || '').trim();
    if (first || last) {
      return `${first[0] || ''}${last[0] || ''}`.toUpperCase() || '?';
    }

    const displayName = getDisplayName(u).trim();
    const parts = displayName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    return (displayName[0] || getEmail(u)[0] || '?').toUpperCase();
  };

  const getAvatarFailureKey = (u: any) => String(u?.id ?? getDisplayName(u));

  const handleAvatarError = (key: string) => {
    setFailedAvatarIds((prev) => ({ ...prev, [key]: true }));
  };

  // 🔹 Users we can still add (available but not already assigned)
  const addableUsers = (() => {
    if (!availableUsers || availableUsers.length === 0) return [];
    const assignedIds = new Set(assignees.map((a) => a.id));
    return availableUsers.filter((u) => !assignedIds.has(u.id));
  })();

  const handleRemoveAssignee = (userId: number | undefined) => {
    if (userId == null) return;
    const next = assignees.filter((a) => a.id !== userId);
    setAssignees(next);

    // Clean up notify flag if they’re removed
    setNotifyOnAdd((prev) => {
      const clone = { ...prev };
      delete clone[userId];
      return clone;
    });
  };

  const handleAddAssignee = () => {
    if (!availableUsers || selectedUserIdToAdd === '') return;

    const user = availableUsers.find((u) => u.id === selectedUserIdToAdd);
    if (!user) return;

    // avoid duplicates
    if (assignees.some((a) => a.id === user.id)) {
      setSelectedUserIdToAdd('');
      return;
    }

    const next = [...assignees, user];
    setAssignees(next);

    // If this is a *new* assignee (not in original), default notifyOnAdd = true
    if (!originalAssigneeIds.includes(user.id)) {
      setNotifyOnAdd((prev) => ({
        ...prev,
        [user.id]: true,
      }));
    }

    setSelectedUserIdToAdd('');
  };

  /**
   * Save assignees to backend and send emails to checked "new" assignees.
   */
  const handleSaveAssignees = async () => {
    if (!ticket?.id) return;

    const assigneeIds = assignees
      .filter((a) => a && a.id != null)
      .map((a) => Number(a.id));

    const newlyAddedIds = assigneeIds.filter(
      (id) => !originalAssigneeIds.includes(id)
    );

    const notifyUserIds = newlyAddedIds.filter((id) => !!notifyOnAdd[id]);
    const loginUniqueId = getLoginUniqueId(window.location.pathname, window.location.search) || undefined;

    const payload = {
      // Stored in DB as [{ id, type: 'user' }, ...] but backend
      // will happily normalize from plain IDs as well.
      assignees: assigneeIds,
      login_unique_id: loginUniqueId,
      notifyUserIds,
    };

    

    try {
      setSavingAssignees(true);
      setAssigneeSaveError(null);
      const response = (await updateTicket(ticket.id, payload)) as
        | { ticket?: Ticket }
        | Ticket
        | undefined;

      const updatedTicket =
        response && 'ticket' in response
          ? response.ticket || null
          : (response as Ticket | undefined) || null;

      // Update "original" ids to whatever we just successfully saved
      setOriginalAssigneeIds(assigneeIds);
      setNotifyOnAdd({});
      setShowAssigneeManager(false);
      if (updatedTicket) {
        onTicketUpdated?.(updatedTicket);
      }

      // Optional: inform parent if it cares
      onChangeAssignees?.(assignees, {
        assigneeIds,
        newlyAddedIds,
        notifyUserIds,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update assignees.';
      setAssigneeSaveError(message);
    } finally {
      setSavingAssignees(false);
    }
  };

  const handleSaveStatus = async () => {
    if (!ticket?.id) return;

    try {
      setSavingStatus(true);
      setStatusSaveError(null);
      const updatedTicket = await updateTicketStatus(ticket.id, { status: statusValue });
      onTicketUpdated?.(updatedTicket);
      onTicketActivity?.(ticket.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update status.';
      setStatusSaveError(message);
    } finally {
      setSavingStatus(false);
    }
  };

  // 🔹 If no ticket selected, just show placeholder
  if (!ticket) {
    return (
      <div className="ticket-details-panel">
        <div className="ticket-details-placeholder">
          Select a ticket to view details
        </div>
      </div>
    );
  }

  return (
    <div className="ticket-details-panel">
      {/* 🔹 Header and core task details (restored) */}
      <div className="page-header no-wrap">
        <h1>
          <span className="extra-heading">
            <i className="fa fa-cog"></i> Task name:
          </span>{' '}
          {ticket.task_name}
        </h1>
      </div>

      <div className="website-details">
        <h3 className="support-ticket-header">Task details</h3>
        <div className="core-command-service-details-inner support-ticket-heading">
          <div className="support-ticket-heading-inner">
            <div className="task-type-label">
              <span>Task type:</span>
              <h3>{formatTaskTypeLabel(ticket.task_type)}</h3>
            </div>
            <div className="task-status-label">
              <span>Task status:</span>
              <div className="ticket-status-control">
                <h3 className={ticket.task_status}>{STATUS_OPTIONS.find((option) => option.value === statusValue)?.label || statusValue}</h3>
                {canUpdateStatus ? (
                  <div className="ticket-status-editor">
                    <select
                      aria-label="Ticket status"
                      value={statusValue}
                      onChange={(event) => setStatusValue(event.target.value as TicketStatus)}
                      disabled={savingStatus}
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="ticket-assignees-manage-btn ticket-status-save-btn"
                      onClick={handleSaveStatus}
                      disabled={savingStatus || statusValue === normalizeTicketStatus(ticket.task_status)}
                    >
                      {savingStatus ? 'Saving...' : 'Save status'}
                    </button>
                  </div>
                ) : null}
                {statusSaveError ? (
                  <div className="login-message error ticket-status-error">{statusSaveError}</div>
                ) : null}
              </div>
            </div>
            <div className="task-urgency-label">
              <span>
                <i className="fa fa-bell"></i> Task urgency:
              </span>
              <h3
                style={{
                  color:
                    ticket.task_urgency === 'emergency' ? 'red' : 'inherit',
                }}
              >
                {ticket.task_urgency}
              </h3>
            </div>
            <div className="task-created-on-label">
              <span>
                <i className="fa fa-calendar"></i> Created on:
              </span>
              <h3>{ticket.createdAt?.substring(0, 10)}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="website-details">
        <h3 className="support-ticket-header">Task description</h3>
        <div className="core-command-service-details-inner support-ticket-heading">
          {ticket.task_description ? (
            <div
              className="message-text"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(ticket.task_description),
              }}
            />
          ) : (
            <p>No description was provided for this task.</p>
          )}
        </div>
      </div>

      {ticket.metadata ? (
        <div className="website-details">
          <h3 className="support-ticket-header">Technical details</h3>
          <div className="core-command-service-details-inner">
            <ul className="support-ticket-meta-list">
              {ticket.metadata.reporterName ? (
                <li>
                  <strong>Reporter:</strong> {ticket.metadata.reporterName}
                </li>
              ) : null}
              {ticket.metadata.reporterEmail ? (
                <li>
                  <strong>Reporter email:</strong> {ticket.metadata.reporterEmail}
                </li>
              ) : null}
              {ticket.metadata.reporterRole ? (
                <li>
                  <strong>Reporter role:</strong> {ticket.metadata.reporterRole}
                </li>
              ) : null}
              {ticket.metadata.pageUrl ? (
                <li>
                  <strong>Reported from:</strong>{' '}
                  {getSafeHttpUrl(ticket.metadata.pageUrl) ? (
                    <a
                      href={getSafeHttpUrl(ticket.metadata.pageUrl) || undefined}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {ticket.metadata.pageUrl}
                    </a>
                  ) : (
                    <span>{ticket.metadata.pageUrl}</span>
                  )}
                </li>
              ) : null}
              {ticket.metadata.pagePath ? (
                <li>
                  <strong>Page path:</strong> {ticket.metadata.pagePath}
                </li>
              ) : null}
              {ticket.metadata.reportedAt ? (
                <li>
                  <strong>Reported at:</strong> {ticket.metadata.reportedAt}
                </li>
              ) : null}
            </ul>
          </div>
        </div>
      ) : null}

      {/* 🔹 Quick Assignees */}
      <div className="ticket-assignees-section">
        <div className="ticket-assignees-header-row">
          <div className="ticket-assignees-title">
            <h3>Assignees</h3>
            <span className="ticket-assignees-subtitle">
              {assigneeCount > 0
                ? `${assigneeCount} ${
                    assigneeCount === 1 ? 'person' : 'people'
                  } assigned`
                : 'No one is assigned to this task yet.'}
            </span>
          </div>

          {canManageAssignees && (
            <button
              type="button"
              className="ticket-assignees-manage-btn"
              onClick={() => setShowAssigneeManager((prev) => !prev)}
            >
              <i className="fa fa-user-edit" />{' '}
              {showAssigneeManager ? 'Close manager' : 'Manage assignees'}
            </button>
          )}
        </div>

        {assigneeCount === 0 ? (
          <div className="ticket-assignees-empty">
            <i className="fa fa-users" />
            <div>
              <strong>No assignees yet.</strong>
              {canManageAssignees && (
                <span> Click “Manage assignees” to assign someone.</span>
              )}
            </div>
          </div>
        ) : (
          <div className="ticket-assignees-pills">
            {assignees.map((a: any) => {
              const name = getDisplayName(a);
              const email = getEmail(a);
              const initials = getInitials(a);
              const avatarFailureKey = getAvatarFailureKey(a);
              const avatarUrl = failedAvatarIds[avatarFailureKey]
                ? null
                : resolveProfilePictureUrl(a.profilePicture ?? a.profile_picture);

              return (
                <div key={a.id || name} className="ticket-assignee-pill">
                  <div className="ticket-assignee-avatar">
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={name}
                        onError={() => handleAvatarError(avatarFailureKey)}
                      />
                    ) : (
                      <span className="ticket-assignee-initials ticket-assignee-avatar-fallback">
                        {initials}
                      </span>
                    )}
                  </div>
                  <div className="ticket-assignee-meta">
                    <span className="ticket-assignee-name">{name}</span>
                    {email && (
                      <span className="ticket-assignee-email">{email}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 🔹 Assignee Manager (add/remove + notify checkbox + save) */}
        {canManageAssignees && showAssigneeManager && (
          <div className="ticket-assignee-manager">
            <div className="ticket-assignee-manager-header">
              <h4>
                <i className="fa fa-user-shield" /> Assignee manager
              </h4>
              <span className="ticket-assignee-manager-hint">
                Add or remove people responsible for this task. You can choose
                which newly-added assignees get an email.
              </span>
            </div>

            <div className="ticket-assignee-manager-body">
              <ul className="ticket-assignee-manager-list">
                {assigneeCount === 0 ? (
                  <li className="ticket-assignee-manager-row empty">
                    No assignees yet. Use the selector below to add someone.
                  </li>
                ) : (
                  assignees.map((a: any) => {
                    const name = getDisplayName(a);
                    const email = getEmail(a);
                    const isNew = !originalAssigneeIds.includes(Number(a.id));

                    return (
                      <li
                        key={a.id || name}
                        className="ticket-assignee-manager-row"
                      >
                        <div className="ticket-assignee-manager-row-main">
                          <div className="ticket-assignee-manager-person">
                            {(() => {
                              const avatarFailureKey = getAvatarFailureKey(a);
                              const avatarUrl = failedAvatarIds[avatarFailureKey]
                                ? null
                                : resolveProfilePictureUrl(a.profilePicture ?? a.profile_picture);

                              return (
                                <span className="ticket-assignee-avatar ticket-assignee-manager-avatar">
                                  {avatarUrl ? (
                                    <img
                                      src={avatarUrl}
                                      alt={name}
                                      onError={() => handleAvatarError(avatarFailureKey)}
                                    />
                                  ) : (
                                    <span className="ticket-assignee-initials ticket-assignee-avatar-fallback">
                                      {getInitials(a)}
                                    </span>
                                  )}
                                </span>
                              );
                            })()}
                            <span className="ticket-assignee-manager-person-copy">
                              <span className="ticket-assignee-manager-name">
                                {name}
                              </span>
                              {email && (
                                <span className="ticket-assignee-manager-email">
                                  {email}
                                </span>
                              )}
                            </span>
                          </div>
                        </div>

                        {isNew && (
                          <label className="ticket-assignee-manager-notify">
                            <input
                              type="checkbox"
                              checked={!!notifyOnAdd[a.id]}
                              onChange={(e) =>
                                setNotifyOnAdd((prev) => ({
                                  ...prev,
                                  [a.id]: e.target.checked,
                                }))
                              }
                            />
                            Email this person about this assignment
                          </label>
                        )}

                        <button
                          type="button"
                          className="ticket-assignee-manager-row-btn"
                          onClick={() => handleRemoveAssignee(a.id)}
                        >
                          <i className="fa fa-user-times" /> Remove
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>

              {/* Add new assignee */}
              <div className="ticket-assignee-manager-add-row">
                {addableUsers.length === 0 ? (
                  <span className="ticket-assignee-manager-add-hint">
                    {availableUsers && availableUsers.length > 0
                      ? 'Everyone in your pool is already assigned.'
                      : 'No additional users are available to assign.'}
                  </span>
                ) : (
                  <>
                    <select
                      aria-label="Select user to add"
                      value={selectedUserIdToAdd === '' ? '' : selectedUserIdToAdd}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedUserIdToAdd(
                          value === '' ? '' : Number(value)
                        );
                      }}
                      className="ticket-assignee-manager-select"
                    >
                      <option value="">Select user to add…</option>
                      {addableUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {getDisplayName(u)} ({getEmail(u)})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="ticket-assignee-manager-row-btn"
                      onClick={handleAddAssignee}
                      disabled={
                        selectedUserIdToAdd === '' || addableUsers.length === 0
                      }
                    >
                      <i className="fa fa-user-plus" /> Add
                    </button>
                  </>
                )}
              </div>

              {/* Save button */}
              <div className="ticket-assignee-manager-footer">
                {assigneeSaveError ? (
                  <div className="login-message error">{assigneeSaveError}</div>
                ) : null}
                <button
                  type="button"
                  className="ticket-assignee-manager-save-btn"
                  onClick={handleSaveAssignees}
                  disabled={savingAssignees}
                >
                  {savingAssignees ? 'Saving…' : 'Save assignees & send emails'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Conversation stays untouched */}
      <Conversation
        commandCentreAuth={commandCentreAuth}
        appLoading={appLoading}
        conversationData={ticket}
        entityDisplayData={entityDisplayData}
        model="website-maintenance"
        onTicketActivity={onTicketActivity}
        onTicketUpdated={onTicketUpdated}
      />
    </div>
  );
};

export default TicketDetailsPanel;
